<script setup lang="ts">
  defineOptions({
    name: 'CPFAB',
  })

  import { computed, useId, type Component, type PropType } from 'vue'

  import CPButton from './CPButton.vue'

  const props = defineProps({
    /**
     * Icon component to display in the FAB
     */
    icon: {
      type: Object as PropType<Component>,
      required: true,
    },
    /**
     * Accessible label for the button (sets aria-label attribute)
     */
    label: {
      type: String,
      required: true,
    },
    /**
     * Size of the FAB
     */
    size: {
      type: String as PropType<'sm' | 'md' | 'lg'>,
      default: 'md',
    },
    /**
     * Color variant of the FAB
     */
    variant: {
      type: String as PropType<'primary' | 'light'>,
      default: 'light',
    },
    /**
     * Whether the FAB is disabled
     */
    disabled: {
      type: Boolean,
      default: false,
    },
  })

  const emit = defineEmits<{
    click: [event: MouseEvent]
  }>()

  const fabId = useId()

  const BUTTON_PX: Record<'sm' | 'md' | 'lg', number> = { sm: 40, md: 48, lg: 56 }

  const ICON_PX: Record<'sm' | 'md' | 'lg', number> = { sm: 20, md: 24, lg: 28 }

  /*
   * CPButton expects explicit CSS colour strings when `color="custom"`. We
   * resolve the brand-aware `--cp-*` tokens here so the FAB tracks the active
   * theme through the global bridge (useTheme.ts + theme.css) without
   * duplicating brand logic in this component.
   */
  const colors = computed(() => {
    if (props.variant === 'primary') {
      return {
        bg: 'var(--cp-primary-600)',
        hoverBg: 'var(--cp-primary-700)',
        text: 'var(--cp-text-on-primary)',
      }
    }
    return {
      bg: 'var(--cp-primary-50)',
      hoverBg: 'var(--cp-primary-100)',
      text: 'var(--cp-primary-600)',
    }
  })

  /*
   * Drive FAB shape + dimensions through naive-ui's own CSS tokens so we stay
   * in sync with the button's internal layout (focus ring geometry, padding
   * maths, etc.). `customStyle` on CPButton is prepended to the rendered
   * `:style` on n-button, so these variables win without needing :deep().
   */
  const fabCustomStyle = computed(() => {
    const px = BUTTON_PX[props.size]
    return [
      `--n-width: ${px}px`,
      `--n-height: ${px}px`,
      `--n-padding: 0`,
      `--n-border-radius: 1000px`,
      `width: ${px}px`,
      `height: ${px}px`,
      `min-width: ${px}px`,
      `padding: 0`,
      `border-radius: 1000px`,
    ].join('; ')
  })

  const iconStyle = computed(() => {
    const px = ICON_PX[props.size]
    return { width: `${px}px`, height: `${px}px` }
  })

  const handleClick = (event: MouseEvent) => {
    emit('click', event)
  }
</script>

<template>
  <CPButton
    :id="fabId"
    type="primary"
    color="custom"
    :custom-style="fabCustomStyle"
    :aria-label="label"
    :disabled="disabled"
    :custom-background-color="colors.bg"
    :custom-hover-background-color="colors.hoverBg"
    :custom-text-color="colors.text"
    :custom-hover-text-color="colors.text"
    :custom-border-color="colors.bg"
    :custom-hover-border-color="colors.hoverBg"
    class="cp-fab"
    :class="{ 'cp-fab--disabled': disabled }"
    @click="handleClick"
  >
    <component :is="icon" class="cp-fab__icon" :style="iconStyle" />
  </CPButton>
</template>

<style scoped>
  .cp-fab {
    box-shadow:
      0 20px 24px -4px rgba(16, 24, 40, 0.08),
      0 8px 8px -4px rgba(16, 24, 40, 0.03);
    transition:
      transform 0.2s ease,
      box-shadow 0.2s ease,
      background-color 0.2s ease;
  }

  .cp-fab:hover:not(.cp-fab--disabled) {
    transform: scale(1.05);
  }

  .cp-fab:active:not(.cp-fab--disabled) {
    transform: scale(0.98);
  }

  .cp-fab:focus-visible {
    outline: 2px solid var(--cp-primary-500);
    outline-offset: 2px;
  }

  .cp-fab--disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .cp-fab__icon {
    display: block;
    color: currentColor;
  }
</style>
